import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Travel } from '../models/travel';
import { TravelExpense } from '../models/travel-expense';
import { TravelLine } from '../models/travel-line';
import { Order } from '../models/order';
import { Set } from '../models/set';
import { ContractedInvoice } from '../models/contracted-invoice';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { GasTravel } from '../models/gas-travel';
import { TravelLineTicket } from '../models/travel-line-ticket';
import { Observable } from 'rxjs';
import { InvoiceTicket } from '../models/invoice-ticket';

@Injectable({
  providedIn: 'root'
})
export class TravelService {

  constructor(private http:HttpClient) { }

  getTravels(params?) : Promise<Travel[]> {
    return this.http.get<Travel[]>('/api/travel', {params}).toPromise();
  }

  getContractedTravels(date1:Date, date2:Date, travelType: number) : Promise<Travel[]> {
    return this.http.get<Travel[]>('/api/travel/contracted', {params: {date1: date1.toString(), date2: date2.toString(), travelType: travelType.toString() }}).toPromise();
  }

  getTravelsByDates(date1:Date, date2:Date, params) : Promise<Travel[]> {
    return this.http.get<Travel[]>('/api/travel/' + date1.toString() + '/' + date2.toString(), {params}).toPromise();
  }

  getTravelsByDatesMarketPriceTravels(date1:Date, date2:Date, params) : Promise<Travel[]> {
    return this.http.get<Travel[]>('/api/travel/market-price/' + date1.toString() + '/' + date2.toString(), {params}).toPromise();
  }

  getTravelByOrder(order:Order) : Promise<Travel> {
    return this.http.get<Travel>('/api/travel/order/' + order.id).toPromise();
  }

  getTravel(travel:number) : Promise<Travel> {
    return this.http.get<Travel>('/api/travel/' + travel).toPromise();
  }

  getCheckEmmit(travel:number,typeofgas:number) : Promise<GasTravel> {
    return this.http.get<GasTravel>('/api/travel/checkemmit/' + travel +'/'+typeofgas).toPromise();
  }

  postTravel(travel:Travel) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel', travel).toPromise();
  }

  postContractedTravel(invoice:ContractedInvoice) : Observable<ContractedInvoice> {
    return this.http.post<ContractedInvoice>('/api/travel/contracted', invoice);
  }

  deleteTravel(travel:Travel, description:string = null) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + travel.id + '/delete', {description}).toPromise();
  }

  deleteGasTravel(gasTravel:GasTravel):Observable<any>{
    return this.http.delete<any>('/api/gas-travel/'+gasTravel.id+'/delete');
  }

  closeTravel(id:number, lines) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + id + '/close', lines).toPromise();
  }

  openTravel(id:number) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + id + '/open', {}).toPromise();
  }

  setDeliveryDate(id:number, date:Date) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + id + '/delivery', {date}).toPromise();
  }
  setPrdLoadDate(id:number, date:Date) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + id + '/prdload', {date}).toPromise();
  }
  setGasLoadDate(id:number, date:Date) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + id + '/gasload', {date}).toPromise();
  }
  setUnities(id:number, set:Set) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + id + '/edit/set', set).toPromise();
  }

  postNewLine(id:number, lines:TravelLine[], expenses:TravelExpense[]) : Promise<Travel> {
    return this.http.post<Travel>('/api/travel/' + id + '/edit/newline', {lines, expenses}).toPromise();
  }

  postPaysheet(travel:Travel, paysheet:any) : Promise<void> {
    return this.http.post<void>("/api/travel/" + travel.id + "/paysheet", paysheet).toPromise();
  }

  postGasTicket(travel:number, ticket:number) : Promise<void> {
    return this.http.post<void>("/api/travel/" + travel + "/gasticket", {ticket}).toPromise();
  }

  postMarketPrice(travel:number, data:{price:number, update_all:boolean, customer_address_id:number}) : Promise<void> {
    return this.http.post<void>("/api/travel/" + travel + "/market-price", data).toPromise();
  }

  saveMarketPrice(date1:Date, date2:Date, type:string) : Promise<void> {
    return this.http.post<void>("/api/travel/market-price", {date1: date1.toString(), date2: date2.toString(), type}).toPromise();
  }

  postRealGas(travel:Travel, custom_travel:any) : Promise<void> {
    return this.http.post<void>("/api/travel/" + custom_travel.id + "/gasticket/emmit", {
        gas_metric_initial: custom_travel.gas_metric_initial,
        gas_metric_final: custom_travel.gas_metric_final,
        gas_date_proeco: custom_travel.gas_date,
        gas_description_proeco: custom_travel.gas_description,
        gas_travel_id : custom_travel.id,
        type : custom_travel.type,
        gas_type : custom_travel.gas_type,
        lts : custom_travel.lts,
        lts_2 : custom_travel.lts_2,
        lts_loaded: custom_travel.lts_loaded,
        lts_loaded_2 : custom_travel.lts_loaded_2
      }).toPromise();
  }

  postGasTicket2(travel:number, quantity:number) : Promise<number> {
    return this.http.post<number>("/api/travel/" + travel + "/gasticket2", {quantity}).toPromise();
  }

  updateTravel(travel : Travel) : Promise<Travel>{
    return this.http.post<Travel>('/api/travel/update/'+travel,{travel}).toPromise();
  }

  transformWMtoTravel(wmpo:number) : Promise<number> {
    return this.http.get<number>('/api/travel/transform/from/wmpo/' + wmpo).toPromise();
  }

  transformANtoTravel(wman:number) : Promise<number> {
    return this.http.get<number>('/api/travel/transform/from/wman/' + wman).toPromise();
  }

  setInvoice(travel:Travel, invoice) : Promise<any>{
    return this.http.post<any>('/api/travel/invoice/' + travel.id, {invoice}).toPromise();
  }

  postTiketLine(ticketLine:TravelLineTicket) : Promise<TravelLineTicket> {
    return this.http.post<TravelLineTicket>('/api/travel/ticket', ticketLine).toPromise();
  }

  getTiketLine(lineId:number) : Promise<TravelLineTicket[]> {
    return this.http.get<TravelLineTicket[]>('/api/travel/ticket/' + lineId).toPromise();
  }

  deleteTicket(ticketLine:TravelLineTicket):Observable<any>{
    return this.http.delete<any>('/api/travel/ticket/'+ticketLine.id+'/delete');
  }

  getInternalTravels(): Observable<Travel[]>{
    return this.http.get<Travel[]>('/api/travel/internal');
  }

  getInternalTravelByID(id: number): Observable<Travel>{
    return this.http.get<Travel>('/api/travel/internal/by-id', {params:{'id': JSON.stringify(id)}});
  }

  getFormatDataByTravelID(travels: number[]):Observable<Travel[]>{
    return this.http.get<Travel[]>('/api/get/selected/travels', {params:{'travels': JSON.stringify(travels)}});
  }

  getTravelById(id: number): Observable<Travel>{
    return this.http.get<Travel>('/api/get/travel/by-id', {params:{'id': JSON.stringify(id)}})
  }

  delTravelRoute(travelLine: TravelLine): Promise<Travel>{
    return this.http.delete<Travel>('/api/travel/line/' + travelLine.id + '/delete').toPromise();
  }

  addTravelLines(line: TravelLine): Promise<Travel>{
    return this.http.post<Travel>('/api/travel/add/newline', line).toPromise();
  }

  updateTravelLines(line: TravelLine): Promise<Travel>{
    return this.http.post<Travel>('/api/travel/' + line.id + '/edit/line', line).toPromise();
  }

  updateMarketPriceByDirections(price: number, source_customer_id: number, source_customer_address_id: number, target_customer_id: number, target_customer_address_id: number):Promise<Travel[]>{
    return this.http.post<Travel[]>('/api/travel/market-price/update', {'price': JSON.stringify(price), 'source_customer_id': JSON.stringify(source_customer_id), 
                                                                        'source_customer_address_id': JSON.stringify(source_customer_address_id), 'target_customer_id': JSON.stringify(target_customer_id),
                                                                        'target_customer_address_id': JSON.stringify(target_customer_address_id)}).toPromise();
  }

  getTravelsRoutesNoRepeat(date1:Date, date2:Date, params) : Promise<Travel[]> {
    return this.http.get<Travel[]>('/api/travel/routes/no-repeat/' + date1.toString() + '/' + date2.toString(), {params}).toPromise();
  }

  getTravelRoutesById(source_customer_id: number, source_customer_address_id: number, target_customer_id: number, target_customer_address_id: number): Promise<Travel[]>{
    return this.http.get<Travel[]>('/api/get-all/id/travel-routes', {params:{'source_customer_id': JSON.stringify(source_customer_id), 'source_customer_address_id': JSON.stringify(source_customer_address_id), 
                                                                    'target_customer_id': JSON.stringify(target_customer_id), 'target_customer_address_id': JSON.stringify(target_customer_address_id)}}).toPromise();
  }

  getTravelRoutesTotal(): Promise<Travel[]>{
    return this.http.get<Travel[]>('/api/get-all/travel-routes').toPromise();
  }

// #region Particualar Drivers
  postParticularTravel(travel:any) : Promise<any> {
    return this.http.post<any>('/api/particular/travel', travel).toPromise();
  }
// #endregion

}
